import React, {
  useMemo, useCallback, useEffect, useState, Fragment, ReactElement,
} from 'react';
import { useNavigate } from 'react-router';
import Table from '../../../../../../components/table/table.component';
import TableHead from '../../../../../../components/table-head/table-head.component';
import TableBody from '../../../../../../components/table-body/table-body.component';
import TableRow from '../../../../../../components/table-row/table-row.component';
import TableRowHelper from '../../../../../../components/table-row-helper/table-row-helper.component';
import TableRowHidden from '../../../../../../components/table-row-hidden/table-row-hidden.component';
import TableCell from '../../../../../../components/table-cell/table-cell.component';
import Chip from '../../../../../../components/chip/chip.component';
import ChipLink from '../../../../../../components/chip-link/chip-link.component';
import Button from '../../../../../../components/button/button.component';
import Hint from '../../../../../../components/hint/hint.component';
import TextBold from '../../../../../../components/text-bold/text-bold.component';
import FieldText from '../../../../../../components/field-text/field-text.component';
import FlexContainer from '../../../../../../components/flex-container/flex-container.component';
import FilteredLink from '../../../../../../components/filtered-link/filtered-link.component';
import RequiredMark from '../../../../../../components/required-mark/required-mark.component';
import ConfirmModal from '../../../../../../components/confirm-modal/confirm-modal.component';
import ConnectOtpModal from '../../../../../../components/connect-otp-modal/connect-otp-modal.component';
import TextBlock from '../../../../../../components/text-block/text-block.component';
import ActionBlock from '../../../../../../components/action-block/action-block.component';
import FieldUpload from '../../../../../../components/field-upload/field-upload.component';
import SecretKeyBlock from '../../../../../../components/secret-key-block/secret-key-block.component';
import FormError from '../../../../../../components/form-error/form-error.component';
import FormFieldSelect from '../../../../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect
  from '../../../../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldPassword from '../../../../../../components/form-field-password/form-field-password.component';
import FormFieldCheckbox from '../../../../../../components/form-field-checkbox/form-field-checkbox.component';
import MenuItemList from '../../../../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../../../../app/common/single-page-tab/single-page-tab.component';
import ScreenService from '../../../../../../services/screen-service';
import StructureService from '../../../../../../services/structure-service';
import FormatService from '../../../../../../services/format-service';
import FormService from '../../../../../../services/form-service';
import useScreenSmall from '../../../../../../hooks/use-screen-small';
import useScreenMobile from '../../../../../../hooks/use-screen-mobile';
import { ROUTE_DEAL, ROUTE_USER } from '../../../../../../constants/system/routes';
import { DEAL_PARTICIPANTS_VIEW, SETTINGS_USER_VIEW } from '../../../../../../constants/entity/permissions';
import { FORMAT_LIST_IMPORT_SETTINGS } from '../../../../../../constants/system/format-lists';
import {
  ACCOUNT_USER_TYPE_ADMIN, ACCOUNT_USER_TYPE_SYSTEM_ADMIN,
} from '../../../../../../constants/entity/account-user-types';
import {
  ACCOUNT_USER_TYPE_LIST_ADMIN, ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED, ACCOUNT_USER_TYPE_LIST_PORTFOLIO_LIMITED,
  ACCOUNT_USER_TYPE_LIST_TRUSTED,
} from '../../../../../../constants/entity/account-user-type-lists';
import { QUERY_PARAM_TAB } from '../../../../../../constants/system/query-params';
import { TAB_NAME_PARTICIPANTS } from '../../../../../../constants/system/tab-names';
import {
  VERIFICATION_TYPE_AUTHENTICATOR_APP_OTP, VERIFICATION_TYPE_PHONE_NUMBER_SMS,
} from '../../../../../../constants/entity/verification-types';
import ACCOUNT_USER_TYPE_GROUP_EXTERNAL from '../../../../../../constants/entity/account-user-type-groups';
import AssetDefaultImage from '../../../../../../static/images/asset-default-image.png';

const SettingsUserSecuritySummary: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 2;
  const PORTFOLIO_HIDDEN_LABEL: string = 'Hidden';
  const USER_VERIFICATION_TYPE_DEFAULT_LABEL: string = 'Auto';

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    isCurrentUser,
    isUserApiKeyChanged,
    formError,
    currentUser,
    userData,
    otpSecretHash,
    otpSecretQrCodeLink,
    importExportErrorMessage,
    connectOtpModalErrorMessage,
    dealList,
    portfolioList,
    hiddenPortfolioList,
    accountUserTypeList,
    accessLevelList,
    verificationTypeList,
    onCancelForm,
    onSubmitForm,
    onResetSettings,
    onExportSettings,
    onImportSettings,
    onImportSettingsFileDelete,
    onAccountUserTypeChange,
    onAccountUserDealsChange,
    onAccountUserPortfoliosChange,
    onAccountUserDealAccessLevelChange,
    onDealSearch,
    onDealSearchClose,
    onGetUserApiKey,
    onCopyUserApiKey,
    onConnectOtpData,
    onVerifyOtpSecretData,
    onDeleteOtpSecretData,
    onGetOtpSecretData,
    onVerifyPhone,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();
  const isMobileScreen: boolean = useScreenMobile();
  const navigate: ReactRouterNavigate = useNavigate();

  const [isPasswordVisible, setIsPasswordVisible]: [boolean, Function] = useState(false);
  const [isPasswordConfirmVisible, setIsPasswordConfirmVisible]: [boolean, Function] = useState(false);
  const [isOldPasswordVisible, setIsOldPasswordVisible]: [boolean, Function] = useState(false);
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);
  const [importFile, setImportFile]: [File | null, Function] = useState(null);
  const [isDealAccessLevelContainerVisible, setIsDealAccessLevelContainerVisible]: [boolean, Function]
    = useState(false);
  const [isVerificationTypesContainerVisible, setIsVerificationTypesContainerVisible]: [boolean, Function]
    = useState(false);
  const [selectedDeals, setSelectedDeals]: [Deal[] | null, Function] = useState<StateValueDefault>(null);
  const [selectedPortfolios, setSelectedPortfolios]: [Portfolio[] | null, Function] = useState<StateValueDefault>(null);
  const [accountUserType, setAccountUserType]: [AccountUserType | null, Function] = useState(null);
  const [accountUserTypeGroup, setAccountUserTypeGroup]: [AccountUserTypeGroup | null, Function] = useState(null);
  const [isVerificationRequired, setIsVerificationRequired]: [boolean | null, Function] = useState(null);
  const [isConnectOtpModalOpen, setIsConnectOtpModalOpen]: [boolean, Function] = useState(false);
  const [isDeleteOtpSecretConfirmModalOpen, setIsDeleteOtpSecretConfirmModalOpen]: [boolean, Function]
    = useState(false);

  const commonPortfolioList: Portfolio[] = useMemo((): Portfolio[] => [
    ...(portfolioList || []),
    ...(hiddenPortfolioList ? hiddenPortfolioList.map((portfolioItem: Portfolio): Portfolio => ({
      ...portfolioItem,
      name: `${portfolioItem.name} (${PORTFOLIO_HIDDEN_LABEL})`,
    })) : []),
  ], [portfolioList, hiddenPortfolioList]);

  const isCurrentUserSystemAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && currentUser.accountUserType === ACCOUNT_USER_TYPE_SYSTEM_ADMIN;
  const isCurrentUserAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && ACCOUNT_USER_TYPE_LIST_ADMIN.includes(currentUser.accountUserType);
  const isUserAdmin: boolean = !!userData && !!userData.accountUserType
    && ACCOUNT_USER_TYPE_LIST_ADMIN.includes(userData.accountUserType);
  const isTrustedUserView: boolean = !!accountUserType && ACCOUNT_USER_TYPE_LIST_TRUSTED.includes(accountUserType);
  const isAccessLevelView: boolean = !!accountUserTypeGroup
    && accountUserTypeGroup === ACCOUNT_USER_TYPE_GROUP_EXTERNAL;
  const userIsPhoneVerified: boolean = (!!userData && userData.isPhoneVerified) || false;
  const userIsOtpEnabled: boolean = (!!userData && userData.isOtpEnabled) || false;
  const userIsOtpVerified: boolean = (!!userData && userData.isOtpVerified) || false;
  const userApiKey: string | null = (userData && userData.apiKey) || null;
  const userPhone: string | null = (userData && userData.phone) || null;
  const accountUserTypeMap: Record<string, AccountUserType> = useMemo((): Record<string, AccountUserType> => (
    StructureService.getItemMap(accountUserTypeList, 'name')
  ), [accountUserTypeList]);
  const verificationTypeMap: Record<string, AccountUserType> = useMemo((): Record<string, AccountUserType> => (
    StructureService.getItemMap(verificationTypeList, 'name')
  ), [verificationTypeList]);
  const userVerificationTypeDefault: VerificationType | null = userData && userData.verificationTypeDefault
  && verificationTypeMap ? (verificationTypeMap[userData.verificationTypeDefault]) : null;
  const userVerificationTypeDefaultText: string = (userVerificationTypeDefault && userVerificationTypeDefault.title)
    || USER_VERIFICATION_TYPE_DEFAULT_LABEL;
  const isMultiplePortfolioGroupView: boolean = useMemo((): boolean => (
    commonPortfolioList && commonPortfolioList.length ? commonPortfolioList
      .map((portfolioItem: Portfolio): number => portfolioItem.group && portfolioItem.group.id)
      .filter((portfolioGroupId: number, portfolioGroupIndex: number, portfolioItemList: number[]): boolean => (
        portfolioItemList.indexOf(portfolioGroupId) === portfolioGroupIndex
      )).length > 1 : false
  ), [commonPortfolioList]);
  const filteredAccountUserTypeList: AccountUserType[] = useMemo((): AccountUserType[] => (
    accountUserTypeList && !!accountUserTypeList.length && !isUserAdmin && !isCurrentUserSystemAdmin
      ? accountUserTypeList.filter((accountUserTypeItem: AccountUserType): boolean => (
        accountUserTypeItem.name !== ACCOUNT_USER_TYPE_ADMIN
      )) : accountUserTypeList
  ), [accountUserTypeList, isUserAdmin, isCurrentUserSystemAdmin]);
  const filteredVerificationTypeList: VerificationType[] = useMemo((): VerificationType[] => (
    verificationTypeList && !!verificationTypeList.length && (
      verificationTypeList.filter((verificationTypeItem: VerificationType): boolean => (
        (verificationTypeItem.name === VERIFICATION_TYPE_PHONE_NUMBER_SMS && userIsPhoneVerified)
        || (verificationTypeItem.name === VERIFICATION_TYPE_AUTHENTICATOR_APP_OTP && userIsOtpVerified)
      ))
    )
  ), [verificationTypeList, userIsPhoneVerified, userIsOtpVerified]);
  const userAccountUserType: AccountUserType | null = accountUserTypeMap
    ? accountUserTypeMap[userData.accountUserType] : null;
  const formPrefix: string = 'accountUserDealAccessLevels';
  const deleteOtpSecretConfirmModalTitle: string = 'Delete Authenticator Data';
  const deleteOtpSecretConfirmModalText: string = 'Are you sure you want to delete Authenticator App data? '
    + 'This MFA method won\'t be available until you connect it to the app again.';

  const menuItemAccountUserTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: true,
    groupKey: 'group',
    groupItemLabelKey: 'title',
    optionItemValueKey: 'name',
    optionList: filteredAccountUserTypeList,
  }), [filteredAccountUserTypeList]);

  const menuItemAccessLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: accessLevelList,
  }), [accessLevelList]);

  const menuItemDealList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: dealList,
    optionItemImageKey: 'mainPhoto',
    optionItemImageThumbnailKey: 'mainPhotoThumbnail',
    defaultImage: AssetDefaultImage,
    selectedItemList: selectedDeals,
  }), [dealList, selectedDeals]);

  const menuItemPortfolioList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: isMultiplePortfolioGroupView,
    groupKey: 'group',
    groupItemLabelKey: 'name',
    optionItemLabelKey: 'name',
    optionList: commonPortfolioList,
    selectedItemList: selectedPortfolios,
  }), [commonPortfolioList, selectedPortfolios, isMultiplePortfolioGroupView]);

  const menuItemVerificationTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: filteredVerificationTypeList,
  }), [filteredVerificationTypeList, userIsPhoneVerified, userIsOtpVerified]);

  const onPasswordVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordVisible(value);
  }, []);

  const onOldPasswordVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsOldPasswordVisible(value);
  }, []);

  const onPasswordConfirmVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordConfirmVisible(value);
  }, []);

  const resetSettings: MouseEventHandlerFunction = useCallback((): void => {
    if (onResetSettings) {
      onResetSettings();
    }
  }, []);

  const exportSettings: MouseEventHandlerFunction = useCallback((): void => {
    if (onExportSettings) {
      onExportSettings();
    }
  }, []);

  const importSettings: MouseEventHandlerFunction = useCallback((): void => {
    if (onImportSettings && importFile) {
      onImportSettings(importFile, (): void => {
        setImportFile(null);
      });
    }
  }, [importFile]);

  const onImportFileChange: Function = useCallback((file: File): void => {
    setImportFile(file);
    setErrorMessage(null);
  }, []);

  const onImportFileDelete: Function = useCallback((): void => {
    setImportFile(null);
    setErrorMessage(null);

    if (onImportSettingsFileDelete) {
      onImportSettingsFileDelete();
    }
  }, []);

  const onImportFileError: Function = useCallback((error: string): void => {
    setErrorMessage(error);
  }, []);

  const getUserApiKey: MouseEventHandlerFunction = useCallback((): void => {
    if (onGetUserApiKey) {
      onGetUserApiKey();
    }
  }, []);

  const copyUserApiKey: MouseEventHandlerFunction = useCallback(async (): Promise<void> => {
    if (userApiKey) {
      await ScreenService.copyToClipboard(userApiKey);

      if (onCopyUserApiKey) {
        onCopyUserApiKey();
      }
    }
  }, [userApiKey]);

  const accountUserDealsChange: Function = useCallback((dealValues: Deal[]): void => {
    const removedDealValues: Deal[] = (selectedDeals || []).filter((dealValue: Deal): boolean => (
      !dealValues.find((newDealValue: Deal): boolean => newDealValue.id === dealValue.id)
    ));

    if (onAccountUserDealsChange) {
      onAccountUserDealsChange(dealValues, removedDealValues);
    }

    if (!isDealAccessLevelContainerVisible) {
      setIsDealAccessLevelContainerVisible(true);
    }

    setSelectedDeals(dealValues);
  }, [selectedDeals, isDealAccessLevelContainerVisible]);

  const accountUserPortfoliosChange: Function = useCallback((portfolioValues: Portfolio[]): void => {
    if (onAccountUserPortfoliosChange) {
      onAccountUserPortfoliosChange(portfolioValues);
    }

    setSelectedPortfolios(portfolioValues);
  }, []);

  const accountUserTypeChange: Function = useCallback((accountUserTypeName: string): void => {
    const accountUserTypeListItem: AccountUserType = accountUserTypeList.find((
      accountUserTypeItem: AccountUserType,
    ): boolean => (
      accountUserTypeItem.name === accountUserTypeName
    ));
    const accountUserTypeGroupName: string | null = accountUserTypeListItem.group
      ? accountUserTypeListItem.group.name : null;

    setAccountUserType(accountUserTypeName);
    setAccountUserTypeGroup(accountUserTypeGroupName);

    if (!ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED.includes(accountUserTypeName)) {
      setSelectedDeals(null);
    }

    if (!ACCOUNT_USER_TYPE_LIST_PORTFOLIO_LIMITED.includes(accountUserTypeName)) {
      setSelectedPortfolios(null);
    }

    if (onAccountUserTypeChange) {
      onAccountUserTypeChange(accountUserTypeName, accountUserTypeGroupName);
    }
  }, [userData, accountUserTypeList]);

  const accountUserDealAccessLevelChange: Function = useCallback((
    accessLevelName: string, dealId: number,
  ): void => {
    if (onAccountUserDealAccessLevelChange) {
      onAccountUserDealAccessLevelChange(dealId, accessLevelName);
    }
  }, []);

  const getOtpSecretData: Function = useCallback((): void => {
    setIsConnectOtpModalOpen(true);

    if (onGetOtpSecretData) {
      onGetOtpSecretData();
    }
  }, []);

  const connectOtpButtonClick: Function = useCallback((): void => {
    setIsConnectOtpModalOpen(true);

    if (onConnectOtpData) {
      onConnectOtpData();
    }
  }, []);

  const closeConnectOtpModal: Function = useCallback((): void => {
    setIsConnectOtpModalOpen(false);
  }, []);

  const deleteOtpSecretButtonClick: Function = useCallback((): void => {
    setIsDeleteOtpSecretConfirmModalOpen(true);
  }, []);

  const closeDeleteOtpSecretConfirmModal: Function = useCallback((): void => {
    setIsDeleteOtpSecretConfirmModalOpen(false);
  }, []);

  const deleteOtpSecretConfirm: Function = useCallback((): void => {
    if (onDeleteOtpSecretData) {
      onDeleteOtpSecretData((): void => {
        closeDeleteOtpSecretConfirmModal();
      });
    }
  }, []);

  const onVerifyOtpSecretConfirm: Function = useCallback((verificationCode: string, onSuccess: Function): void => {
    if (onVerifyOtpSecretData) {
      onVerifyOtpSecretData(verificationCode, (): void => {
        closeConnectOtpModal();

        if (onSuccess) {
          onSuccess();
        }
      });
    }
  }, [userData]);

  const verifyPhone: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    if (onVerifyPhone) {
      onVerifyPhone(userPhone);
    }

    event.stopPropagation();
  }, [userPhone]);

  const switchDealAccessLevelContainerVisibility: Function = useCallback((): void => {
    setIsDealAccessLevelContainerVisible(!isDealAccessLevelContainerVisible);
  }, [isDealAccessLevelContainerVisible]);

  const switchVerificationTypesContainerVisibility: Function = useCallback((): void => {
    setIsVerificationTypesContainerVisible(!isVerificationTypesContainerVisible);
  }, [isVerificationTypesContainerVisible]);

  const onDealParticipantsClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealId: number,
  ): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_TAB]: TAB_NAME_PARTICIPANTS,
    };

    navigate({
      pathname: `${ROUTE_DEAL}/${dealId}`,
      search: FormatService.formatQueryParams(queryParams),
    });

    event.stopPropagation();
  }, []);

  const onVerificationRequiredChange: Function = useCallback((isVerificationRequiredValue: boolean): void => {
    setIsVerificationRequired(isVerificationRequiredValue);
  }, []);

  const dealAccessLevelClickDeps: DepsItem[] = useMemo((): DepsItem[] => [
    isDealAccessLevelContainerVisible,
  ], [isDealAccessLevelContainerVisible]);

  const verificationTypesClickDeps: DepsItem[] = useMemo((): DepsItem[] => [
    isVerificationTypesContainerVisible,
  ], [isVerificationTypesContainerVisible]);

  useEffect((): void => {
    if (!selectedDeals && userData) {
      setSelectedDeals(userData.accountUserDeals || []);
    }

    if (!selectedPortfolios && userData) {
      setSelectedPortfolios(userData.accountUserPortfolios || []);
    }

    if (!accountUserType && userData) {
      setAccountUserType(userData.accountUserType);
    }

    if (!accountUserTypeGroup && userData) {
      setAccountUserTypeGroup(userData.accountUserTypeGroup);
    }

    if (isVerificationRequired === null && userData) {
      setIsVerificationRequired(userData.isVerificationRequired);
    }
  }, [userData]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isEmpty={!userData}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          {
            !isCurrentUser && isCurrentUserAdmin && (isEditMode || userData.accountUserType) && (
              <TableRow>
                <TableCell>
                  Type
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        isDisabled={((filteredAccountUserTypeList && !filteredAccountUserTypeList.length)
                          || (isUserAdmin && !isCurrentUserSystemAdmin))}
                        name="accountUserType"
                        optionsTotal={filteredAccountUserTypeList && filteredAccountUserTypeList.length}
                        onChange={((accountUserTypeName: string): void => {
                          accountUserTypeChange(accountUserTypeName);
                        })}
                      >
                        {menuItemAccountUserTypeList}
                      </FormFieldSelect>
                    ) : ((
                      !!userAccountUserType && !!userAccountUserType.group && (
                        <Fragment>
                          <TextBold>
                            {userAccountUserType.group.title}
                          </TextBold>
                          :
                          {' '}
                          {userAccountUserType.title}
                        </Fragment>
                      )
                    ) || '')
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            !isCurrentUser && isCurrentUserAdmin
            && ((selectedPortfolios && !selectedPortfolios.length) || !selectedPortfolios)
            && accountUserType && ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED.includes(accountUserType)
            && (isEditMode || (userData.accountUserDeals && !!userData.accountUserDeals.length)) && (
              <TableRow>
                <TableCell>
                  Allowed Deals
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldMultiSelect
                        isBordered
                        withImage
                        withSearch
                        isDisabled={dealList && !dealList.length}
                        blankElementLabel={isTrustedUserView ? 'All' : 'None'}
                        imageKey="mainPhoto"
                        imageThumbnailKey="mainPhotoThumbnail"
                        name="accountUserDeals"
                        optionList={dealList}
                        defaultImage={AssetDefaultImage}
                        onClose={onDealSearchClose}
                        onSearch={onDealSearch}
                        onValuesChange={accountUserDealsChange}
                      >
                        {menuItemDealList}
                      </FormFieldMultiSelect>
                    ) : (
                      userData.accountUserDeals.map((dealItem: Deal): ReactElement => (
                        <ChipLink
                          withImageOffset
                          isDisabled={isEditMode}
                          key={dealItem.id}
                          label={dealItem.name}
                          imageLink={dealItem.mainPhotoThumbnail || dealItem.mainPhoto
                            || AssetDefaultImage}
                          permission={DEAL_PARTICIPANTS_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onDealParticipantsClick(event, dealItem.id);
                          }}
                        />
                      ))
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            !isCurrentUser && isCurrentUserAdmin && ((selectedDeals && !selectedDeals.length) || !selectedDeals)
            && accountUserType && ACCOUNT_USER_TYPE_LIST_PORTFOLIO_LIMITED.includes(accountUserType)
            && (isEditMode || (userData.accountUserPortfolios && !!userData.accountUserPortfolios.length)) && (
              <TableRow>
                <TableCell>
                  Allowed Portfolios
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldMultiSelect
                        isBordered
                        isDisabled={commonPortfolioList && !commonPortfolioList.length}
                        blankElementLabel={isTrustedUserView ? 'All' : 'None'}
                        name="accountUserPortfolios"
                        optionList={commonPortfolioList}
                        onValuesChange={accountUserPortfoliosChange}
                      >
                        {menuItemPortfolioList}
                      </FormFieldMultiSelect>
                    ) : (
                      userData.accountUserPortfolios.map((portfolioItem: Portfolio): ReactElement => (
                        <Chip
                          isDisabled
                          key={portfolioItem.id}
                          label={portfolioItem.name}
                        />
                      ))
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            !isCurrentUser && isCurrentUserAdmin && selectedDeals && !!selectedDeals.length && (
              <TableRow>
                <TableCell colSpan={COLUMNS_TOTAL}>
                  <FilteredLink
                    isSwitcher
                    withRequiredMark={isAccessLevelView && isEditMode && !isDealAccessLevelContainerVisible}
                    clickDeps={dealAccessLevelClickDeps}
                    onClick={switchDealAccessLevelContainerVisibility}
                  >
                    Deal Access Levels
                  </FilteredLink>
                </TableCell>
              </TableRow>
            )
          }
          {
            !isCurrentUser && isCurrentUserAdmin && selectedDeals && !!selectedDeals.length
            && selectedDeals.map((dealItem: Deal): ReactElement => {
              const accountUserDeal: Deal | null = userData.accountUserDeals && userData.accountUserDeals.length
                ? userData.accountUserDeals.find((accountUserDealItem: Deal): boolean => (
                  accountUserDealItem.id === dealItem.id
                )) : null;
              const accountUserDealAccessLevel: AccessLevel | null = accountUserDeal
                ? accountUserDeal.dealAccessLevel : null;
              const formKeyAccountUserDeal: string = FormService.getIndexedFormFieldKey(dealItem.id);

              return (
                <TableRowHidden isActive={isDealAccessLevelContainerVisible} key={dealItem.id}>
                  <TableCell>
                    {dealItem.name}
                    {' '}
                    Access Level
                    {
                      isAccessLevelView && isEditMode && (
                        <RequiredMark />
                      )
                    }
                  </TableCell>
                  <TableCell>
                    {
                      isEditMode ? (
                        <FormFieldSelect
                          isBordered
                          withBlankElement
                          isDisabled={accessLevelList && !accessLevelList.length}
                          name={`${formPrefix}[${formKeyAccountUserDeal}]`}
                          optionsTotal={accessLevelList && accessLevelList.length}
                          onChange={((accessLevelName: string): void => {
                            accountUserDealAccessLevelChange(accessLevelName, dealItem.id);
                          })}
                        >
                          {menuItemAccessLevelList}
                        </FormFieldSelect>
                      ) : (
                        (accountUserDealAccessLevel && accountUserDealAccessLevel.title) || (
                          <TextBlock>
                            None
                          </TextBlock>
                        )
                      )
                    }
                  </TableCell>
                </TableRowHidden>
              );
            })
          }
          {
            isDealAccessLevelContainerVisible && selectedDeals && !!selectedDeals.length && (
              <TableRowHelper />
            )
          }
          {
            !isEditMode && (
              <TableRow>
                <TableCell>
                  Password
                </TableCell>
                <TableCell>
                  {FormatService.formatEncryptedString(null, FormService.PASSWORD_FIELD_LENGTH_MIN)}
                </TableCell>
              </TableRow>
            )
          }
          {
            isCurrentUser && isEditMode && (
              <TableRow>
                <TableCell>
                  Old Password
                </TableCell>
                <TableCell>
                  {
                    currentUser && (
                      <FieldText
                        isPlain
                        isHidden
                        withAutocompleteUserName
                        name="email"
                        value={currentUser.email}
                      />
                    )
                  }
                  <FormFieldPassword
                    isBordered
                    withAutocompleteCurrentPassword
                    isVisible={isOldPasswordVisible}
                    name="oldPassword"
                    onVisibilityChange={onOldPasswordVisibilityChange}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && (
              <TableRow>
                <TableCell>
                  New Password
                </TableCell>
                <TableCell>
                  <FormFieldPassword
                    isBordered
                    withStrengthBar
                    withAutocompleteNewPassword
                    isVisible={isPasswordVisible}
                    name="password"
                    onVisibilityChange={onPasswordVisibilityChange}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && (
              <TableRow>
                <TableCell>
                  Confirm Password
                </TableCell>
                <TableCell>
                  <FormFieldPassword
                    isBordered
                    withNoAutocomplete
                    isVisible={isPasswordConfirmVisible}
                    name="passwordConfirm"
                    onVisibilityChange={onPasswordConfirmVisibilityChange}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            !isCurrentUser && isCurrentUserAdmin && (
              <TableRow>
                <TableCell>
                  Require to change password on next login
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldCheckbox withNoLabelOffset name="isPasswordChangeRequired" />
                    ) : (
                      FormatService.formatBooleanValue(userData.isPasswordChangeRequired)
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            !isCurrentUser && isCurrentUserAdmin && (
              <TableRow>
                <TableCell>
                  Require to enable MFA on next login
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldCheckbox withNoLabelOffset name="isVerificationEnableRequired" />
                    ) : (
                      FormatService.formatBooleanValue(userData.isVerificationEnableRequired)
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            ((((isCurrentUser || isCurrentUserAdmin) && !userData.isVerificationRequired))
              || isCurrentUserSystemAdmin) && (
              <TableRow>
                <TableCell>
                  Multi-Factor Authentication Enabled
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldCheckbox
                        withNoLabelOffset
                        name="isVerificationRequired"
                        onChange={onVerificationRequiredChange}
                      />
                    ) : FormatService.formatBooleanValue(userData.isVerificationRequired)
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            isVerificationRequired && (
              <TableRow>
                <TableCell>
                  <FilteredLink
                    isSwitcher
                    isDisabled={isEditMode}
                    clickDeps={verificationTypesClickDeps}
                    onClick={switchVerificationTypesContainerVisibility}
                  >
                    Default MFA Method
                  </FilteredLink>
                  <Hint
                    title="If Auto value is set, method priority is following: Authenticator App OTP, Phone Number SMS"
                  />
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withBlankElement
                        isDisabled={filteredVerificationTypeList && !filteredVerificationTypeList.length}
                        name="verificationTypeDefault"
                        optionsTotal={filteredVerificationTypeList && filteredVerificationTypeList.length}
                        blankElementLabel="Auto"
                      >
                        {menuItemVerificationTypeList}
                      </FormFieldSelect>
                    ) : userVerificationTypeDefaultText
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            isVerificationRequired && (isVerificationTypesContainerVisible || isEditMode) && (
              <TableRow>
                <TableCell colSpan={COLUMNS_TOTAL}>
                  <Table>
                    <TableHead isSubHeader>
                      <TableRow>
                        <TableCell colSpan={COLUMNS_TOTAL}>
                          Available MFA Methods
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody withBorderedRows>
                      <TableRow>
                        <TableCell>
                          Phone Number SMS
                        </TableCell>
                        <TableCell>
                          <FlexContainer>
                            <ActionBlock
                              isSuccessful={userIsPhoneVerified}
                              labelSuccess="Enabled"
                              labelError={`Not Enabled. Verification by phone can be enabled, `
                                + `if phone number is verified only`}
                              labelButton={!userIsPhoneVerified && !!userPhone && 'Verify'}
                              onButtonClick={verifyPhone}
                            />
                          </FlexContainer>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          Authenticator App OTP
                        </TableCell>
                        <TableCell>
                          <FlexContainer withNoAlign isColumn={isSmallScreen}>
                            <ActionBlock
                              isColumnView={isMobileScreen}
                              isSuccessful={userIsOtpEnabled && userIsOtpVerified}
                              labelSuccess="Enabled"
                              labelError={(!userIsOtpEnabled && 'Not Enabled')
                                || (userIsOtpEnabled && !userIsOtpVerified && 'Enabled, not verified')}
                              labelButton={(!userIsOtpEnabled && 'Enable')
                                || (userIsOtpEnabled && !userIsOtpVerified && 'Verify')}
                              onButtonClick={connectOtpButtonClick}
                            />
                            {
                              userIsOtpEnabled && !isEditMode && (
                                <FlexContainer>
                                  <Button
                                    isText
                                    isActive
                                    isDefault
                                    isExtended={!isMobileScreen}
                                    onClick={deleteOtpSecretButtonClick}
                                  >
                                    Delete Authenticator Data
                                  </Button>
                                </FlexContainer>
                              )
                            }
                            {
                              (userIsOtpEnabled && userIsOtpVerified) && !isEditMode && (
                                <FlexContainer>
                                  <Button
                                    isText
                                    isActive
                                    isDefault
                                    onClick={getOtpSecretData}
                                  >
                                    Show Authenticator Data
                                  </Button>
                                </FlexContainer>
                              )
                            }
                          </FlexContainer>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableCell>
              </TableRow>
            )
          }
          {
            isCurrentUserAdmin && (isEditMode || userApiKey) && (
              <TableRow>
                <TableCell>
                  API Key
                  <Hint title="API Key must be set, if user needs an access to REST API" />
                </TableCell>
                <TableCell isText={!isEditMode}>
                  <SecretKeyBlock
                    isEditMode={isEditMode}
                    isSecretKeyChanged={isUserApiKeyChanged}
                    secretKeyValue={userApiKey}
                    secretKeyFormFieldName="apiKey"
                    onGetSecretKey={getUserApiKey}
                    onCopySecretKey={copyUserApiKey}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            !isCurrentUser && isCurrentUserAdmin && (
              <TableRow>
                <TableCell>
                  Active
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldCheckbox withNoLabelOffset name="active" />
                    ) : FormatService.formatBooleanValue(userData.active)
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            !isCurrentUser && isCurrentUserAdmin && !isEditMode && (
              <TableRow>
                <TableCell>
                  Created
                </TableCell>
                <TableCell>
                  {userData.createdDate}
                  {
                    userData.createdByUser && (
                      <Fragment>
                        {' '}
                        by
                        {' '}
                        <FilteredLink
                          link={`${ROUTE_USER}/${userData.createdByUser.id}`}
                          permission={SETTINGS_USER_VIEW}
                        >
                          {userData.createdByUser.fullName}
                        </FilteredLink>
                      </Fragment>
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && (
              <TableRow>
                <TableCell>
                  Restore settings to defaults
                </TableCell>
                <TableCell>
                  <Button isText isActive isDefault onClick={resetSettings}>
                    Reset Settings
                  </Button>
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && !isCurrentUser && isCurrentUserAdmin && (
              <TableRow>
                <TableCell>
                  Import/Export UI Settings
                </TableCell>
                <TableCell>
                  <FlexContainer>
                    {
                      !importFile && (
                        <div>
                          <Button isText isActive isDefault onClick={exportSettings}>
                            Export Settings
                          </Button>
                        </div>
                      )
                    }
                    <div>
                      <FieldUpload
                        isPlain
                        uploadFile={importFile}
                        error={errorMessage}
                        buttonLabel="Import Settings"
                        acceptedExtensionList={FORMAT_LIST_IMPORT_SETTINGS
                          .map((formatItem: FormatItem): string => formatItem.name)}
                        onFileChange={onImportFileChange}
                        onFileDelete={onImportFileDelete}
                        onError={onImportFileError}
                      />
                    </div>
                    {
                      importFile && (
                        <div>
                          <Button isText isActive isDefault onClick={importSettings}>
                            Import
                          </Button>
                        </div>
                      )
                    }
                  </FlexContainer>
                  {
                    importExportErrorMessage && (
                      <FormError withOffset withShowError={isEditMode} error={importExportErrorMessage} />
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
      <ConfirmModal
        isOpen={isDeleteOtpSecretConfirmModalOpen}
        title={deleteOtpSecretConfirmModalTitle}
        text={deleteOtpSecretConfirmModalText}
        onConfirm={deleteOtpSecretConfirm}
        onCloseModal={closeDeleteOtpSecretConfirmModal}
      />
      <ConnectOtpModal
        isOpen={isConnectOtpModalOpen}
        otpSecretHash={otpSecretHash}
        otpSecretQrCodeLink={otpSecretQrCodeLink}
        onCloseModal={closeConnectOtpModal}
        onVerifyOtpSecretConfirm={onVerifyOtpSecretConfirm}
        errorMessage={connectOtpModalErrorMessage}
      />
    </SinglePageTab>
  );
});

export default SettingsUserSecuritySummary;
